import { Card, Tag, Title, Text, Icon } from '@nimbus-ds/components'
import { MoneyIcon, InvoiceIcon, StatsIcon } from '@nimbus-ds/icons'
import { formatCurrency } from '@/lib/utils'

// Revenue Analytics — Phase 1 (DIMENSIONS-style headline strip, client-side from existing
// store-funnel `tnx_analytics`). The pipeline already segregates off-storefront (POS/manual/
// marketplace/API) out of web revenue, so `total_product_revenue` is web-only and deduped by
// order_id; AOV and revenue-per-session are derived here rather than added to the backend yet.
// See docs/specs/OFF-STOREFRONT-ORDERS-SPEC.md (web vs off-web) and the Revenue Analytics plan.

// Minimal shape we read from `data.tnx_analytics`. Kept loose (optionals) because the same
// object feeds the merchant standup and the consultant Resultados view.
export interface RevenueAnalyticsInput {
  unique_sessions?: number
  total_conversions?: number
  step_analysis?: Record<string, { is_conversion?: boolean; total_sessions?: number }>
  product_analytics?: {
    product_performance?: { total_product_revenue?: number }
  }
  off_storefront_orders?: {
    count: number
    revenue: number
    by_storefront?: Record<string, { count: number; revenue: number }>
  } | null
}

const TYPE_LABELS: Record<string, string> = {
  pos: 'Local / POS',
  form: 'Manuales',
  meli: 'Mercado Libre',
  api: 'API / app',
}
const label = (k: string) => TYPE_LABELS[k] || k

// Web conversions: prefer the explicit total, fall back to the conversion step's sessions
// (same source the funnel headline uses, so AOV reconciles with the displayed conversion count).
function webConversions(a: RevenueAnalyticsInput): number {
  if (typeof a.total_conversions === 'number') return a.total_conversions
  const step = Object.values(a.step_analysis || {}).find((s) => s?.is_conversion === true)
  return step?.total_sessions || 0
}

function Metric({
  icon,
  title,
  value,
  hint,
}: {
  icon: React.ReactNode
  title: string
  value: string
  hint?: string
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        {icon}
        <Text fontSize="caption" as="span">{title}</Text>
      </div>
      <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{value}</Text>
      {hint && <Text fontSize="caption">{hint}</Text>}
    </div>
  )
}

/**
 * @param detailed consultant tier — adds the web-vs-off-web revenue composition. Merchant tier
 *   (default) shows only the three calm headline metrics.
 */
export function RevenueAnalyticsCard({
  analytics,
  detailed = false,
}: {
  analytics?: RevenueAnalyticsInput | null
  detailed?: boolean
}) {
  if (!analytics) return null

  const webRevenue = analytics.product_analytics?.product_performance?.total_product_revenue || 0
  const conversions = webConversions(analytics)
  const sessions = analytics.unique_sessions || 0
  const off = analytics.off_storefront_orders

  // Nothing to show if there's no web revenue and no off-web orders.
  if (webRevenue <= 0 && (!off || off.count <= 0)) return null

  const aov = conversions > 0 ? webRevenue / conversions : null
  const rps = sessions > 0 ? webRevenue / sessions : null
  const offTypes = Object.entries(off?.by_storefront || {}).sort((a, b) => b[1].revenue - a[1].revenue)

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <Icon source={<MoneyIcon />} />
          <Title as="h4">Ingresos</Title>
        </div>
        <Text>Ventas de tu tienda online en el período (no incluye ventas no-web).</Text>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Metric
              icon={<Icon source={<MoneyIcon />} />}
              title="Ingresos web"
              value={formatCurrency(webRevenue)}
              hint={`${conversions.toLocaleString()} ${conversions === 1 ? 'venta' : 'ventas'}`}
            />
            <Metric
              icon={<Icon source={<InvoiceIcon />} />}
              title="Ticket promedio"
              value={aov !== null ? formatCurrency(aov) : '—'}
              hint="Ingresos / ventas"
            />
            <Metric
              icon={<Icon source={<StatsIcon />} />}
              title="Ingreso por sesión"
              value={rps !== null ? formatCurrency(rps) : '—'}
              hint={`${sessions.toLocaleString()} sesiones`}
            />
          </div>

          {off && off.count > 0 && (
            <Text fontSize="caption">
              + {formatCurrency(off.revenue)} en {off.count} {off.count === 1 ? 'pedido' : 'pedidos'} no-web
              (POS / manuales / marketplace), excluidos de los ingresos web.
            </Text>
          )}

          {detailed && off && off.count > 0 && offTypes.length > 0 && (
            <div className="space-y-2 border-t pt-3">
              <Text fontSize="caption" color="neutral-textHigh" fontWeight="medium">Ingresos no-web por canal (consultor)</Text>
              <div className="flex flex-wrap gap-2">
                {offTypes.map(([k, v]) => (
                  <Tag key={k} appearance="neutral">
                    {label(k)}: {formatCurrency(v.revenue)} ({v.count})
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}
